import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Setting } from './settings-service';

interface VerseEntry {
  osis: string;
  text: string;
}

interface PinnedEntry {
  osis: string;
  text: string;
  note: string;
}

@customElement('app-verse-panel')
export class VersePanel extends LitElement {
  createRenderRoot = () => this

  private _history = new Setting<VerseEntry[]>('verse-history', [])
  private _pinned = new Setting<PinnedEntry[]>('verse-pinned', [])

  @property({ type: Array }) history: VerseEntry[] = this._history.get()
  @property({ type: Array }) pinned: PinnedEntry[] = this._pinned.get()

  addEntry(osis: string, text: string) {
    this.history = [{ osis, text }, ...this.history.filter(e => e.osis !== osis)].slice(0, 100)
    this._history.set(this.history)
    this.requestUpdate()
  }

  private _pin(entry: VerseEntry) {
    if (this.pinned.some(p => p.osis === entry.osis)) return
    this.pinned = [...this.pinned, { osis: entry.osis, text: entry.text, note: '' }]
    this._pinned.set(this.pinned)
  }

  private _unpin(osis: string) {
    this.pinned = this.pinned.filter(p => p.osis !== osis)
    this._pinned.set(this.pinned)
  }

  private _updateNote(osis: string, note: string) {
    const updated = this.pinned.map(p => p.osis === osis ? { ...p, note } : p)
    this._pinned.set(updated)
    this.pinned = updated
  }

  private _jump(osis: string) {
    this.dispatchEvent(new CustomEvent('verse-jump', {
      bubbles: true, composed: true,
      detail: { osis }
    }))
  }

  private _formatRef(osis: string) {
    const [book, ch, v] = osis.split('.')
    return `${book} ${ch}:${v}`
  }

  render() {
    const pinnedSet = new Set(this.pinned.map(p => p.osis))
    const unpinnedHistory = this.history.filter(e => !pinnedSet.has(e.osis))

    return html`
      ${this.pinned.map(p => html`
        <div class="panel-row pinned">
          <button class="ref-btn" @click=${() => this._jump(p.osis)}>${this._formatRef(p.osis)}</button>
          <span class="verse-text">${p.text}</span>
          <input type="text" .value=${p.note} placeholder="note..."
            @change=${(e: Event) => this._updateNote(p.osis, (e.target as HTMLInputElement).value)}>
          <button @click=${() => this._unpin(p.osis)}>unpin</button>
        </div>
      `)}
      ${this.pinned.length && unpinnedHistory.length ? html`<hr style="margin: 2px 0;">` : ''}
      ${unpinnedHistory.map(e => html`
        <div class="panel-row">
          <button class="ref-btn" @click=${() => this._jump(e.osis)}>${this._formatRef(e.osis)}</button>
          <span class="verse-text">${e.text}</span>
          <button @click=${() => this._pin(e)}>pin</button>
        </div>
      `)}
    `
  }
}
